/**
 * MasterWithdrawPage — J-1-1. 마스터 계정 탈퇴
 */
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Alert, Button, Card, Checkbox, Input, Page, PageHeader, Skeleton } from '@indipia/ui-core';
import { useAuthStore } from '@indipia/shared';
import {
  fetchWithdrawalCheck,
  fetchProfile,
  withdrawUser,
  verifyPassword,
  requestPhoneOtp,
  verifyPhoneOtp,
} from '../api/profile';

function formatPhone(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  if (digits.length <= 10) return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
}

export function MasterWithdrawPage() {
  const navigate   = useNavigate();
  const logout     = useAuthStore((s) => s.logout);
  const user       = useAuthStore((s) => s.user);
  const { data: check, isLoading } = useQuery({
    queryKey: ['withdrawal-check'],
    queryFn: fetchWithdrawalCheck,
  });

  const { data: profile, isLoading: profileLoading } = useQuery({
    queryKey: ['profile'],
    queryFn: fetchProfile,
  });

  // profile 로딩 중에는 SNS 여부를 확정하지 않는다.
  // 미확정 상태로 분기하면 SNS 계정에 비밀번호 폼이 먼저 그려졌다가 OTP 폼으로 교체된다 (D-07 §17.1).
  const isSns = profile?.snsYn === 'Y';

  // 본인 확인 상태
  const [showVerify, setShowVerify]     = useState(false);
  const [verified,   setVerified]       = useState(false);
  const [agreed,     setAgreed]         = useState(false);
  const [verifyError, setVerifyError]   = useState<string | null>(null);
  const [submitError, setSubmitError]   = useState<string | null>(null);

  // SNS — OTP
  const [mobile,      setMobile]      = useState('');
  const [mobileError, setMobileError] = useState<string | null>(null);
  const [otpSent,     setOtpSent]     = useState(false);
  const [otpCode,     setOtpCode]     = useState('');
  const [otpSending,  setOtpSending]  = useState(false);

  // 일반 — 비밀번호
  const [password, setPassword] = useState('');

  const canWithdraw     = check?.activeEmployeeCount === 0;
  const employeeCount   = check?.activeEmployeeCount ?? 0;

  // 탈퇴 실행
  const withdrawMutation = useMutation({
    mutationFn: () => withdrawUser(),
    onSuccess: () => {
      logout();
      navigate('/login', { replace: true });
    },
    onError: () => setSubmitError('탈퇴 처리에 실패했습니다. 다시 시도해주세요.'),
  });

  // 비밀번호 본인확인
  async function handlePasswordVerify() {
    if (!password) { setVerifyError('비밀번호를 입력해주세요.'); return; }
    setVerifyError(null);
    try {
      const ok = await verifyPassword(password);
      if (ok) setVerified(true);
      else setVerifyError('비밀번호가 일치하지 않습니다.');
    } catch {
      setVerifyError('본인 확인에 실패했습니다. 다시 시도해주세요.');
    }
  }

  function handleMobileBlur() {
    if (!mobile) return;
    const registered = profile?.mobile;
    if (!registered) return;
    if (mobile !== registered) {
      setMobileError('가입하신 휴대폰번호와 일치하지 않습니다.');
    } else {
      setMobileError(null);
    }
  }

  // OTP 발송
  async function handleOtpSend() {
    if (!mobile) { setVerifyError('휴대폰번호를 입력해주세요.'); return; }
    const registered = profile?.mobile;
    if (registered && mobile !== registered) {
      setMobileError('가입하신 휴대폰번호와 일치하지 않습니다.');
      return;
    }
    setVerifyError(null);
    setMobileError(null);
    setOtpSending(true);
    try {
      await requestPhoneOtp(mobile);
      setOtpSent(true);
    } catch {
      setVerifyError('인증번호 발송에 실패했습니다.');
    } finally {
      setOtpSending(false);
    }
  }

  // OTP 인증
  async function handleOtpVerify() {
    if (!otpCode) { setVerifyError('인증번호를 입력해주세요.'); return; }
    setVerifyError(null);
    try {
      const res = await verifyPhoneOtp({ mobile, verifyCode: otpCode });
      if (res.verified) setVerified(true);
      else setVerifyError('인증번호가 일치하지 않습니다.');
    } catch (err: unknown) {
      const code = (err as { response?: { data?: { code?: string } } })?.response?.data?.code;
      if (code === 'CODE_MISMATCH') setVerifyError('인증번호가 일치하지 않습니다.');
      else if (code === 'CODE_EXPIRED') setVerifyError('인증번호가 만료됐어요. 다시 발송해주세요.');
      else setVerifyError('인증에 실패했습니다.');
    }
  }

  function handleWithdraw() {
    if (!verified) { setSubmitError('본인 확인이 필요합니다.'); return; }
    if (!agreed)   { setSubmitError('탈퇴 동의를 체크해주세요.'); return; }
    setSubmitError(null);
    withdrawMutation.mutate();
  }

  return (
    <Page width="sm">
      <PageHeader title="마스터 계정 탈퇴" subtitle="탈퇴 전 안내 사항을 꼭 확인해 주세요" />

      {/* 경고 */}
      <Alert kind="warning" title="탈퇴 전 꼭 확인하세요" className="mb-4">
        <p>탈퇴 후 30일간 데이터 보관 후 영구 삭제됩니다.</p>
        <p>업체 서비스가 해지되며, 소속 직원 계정의 탈퇴 혹은 소속 해제 이후 가능합니다.</p>
      </Alert>

      {/* 소속 직원 현황 카드 */}
      {isLoading ? (
        <Skeleton height={90} className="mb-4" />
      ) : (
        <Card className="mb-4">
          <p className="mb-1.5 text-caption font-bold uppercase tracking-widest text-text-muted">소속 직원 현황</p>
          <p className="text-sm font-semibold text-text">
            {canWithdraw
              ? '소속 직원 0명으로 탈퇴 가능합니다.'
              : `직원 ${employeeCount}명의 소속해제 후 탈퇴 가능합니다.`}
          </p>
        </Card>
      )}

        {/* 탈퇴하기 버튼 — 부정 액션이라 etc. danger 는 표시 전용이라 확정 버튼에 쓰지 않는다 (D-09 §15.1) */}
        {!showVerify && (
          <Button
            variant="etc"
            size="lg"
            disabled={!canWithdraw}
            onClick={() => setShowVerify(true)}
            className="mb-4 w-full"
          >
            탈퇴하기
          </Button>
        )}

        {/* 본인 확인 영역 */}
        {showVerify && !verified && (
          <Card className="mb-4">
            <div className="space-y-3">
            <p className="text-sm font-bold text-text">본인 확인</p>

            {profileLoading ? (
              <Skeleton height={40} />
            ) : isSns ? (
              <>
                <div className="flex items-start gap-2">
                  <Input
                    value={mobile}
                    onChange={(e) => { setMobile(formatPhone(e.target.value)); setMobileError(null); }}
                    onBlur={handleMobileBlur}
                    placeholder="010-0000-0000"
                    width="fill"
                    error={mobileError ?? undefined}
                    disabled={otpSent}
                  />
                  <Button
                    variant="secondary"
                    onClick={handleOtpSend}
                    disabled={otpSent || otpSending}
                    loading={otpSending}
                    className="shrink-0"
                  >
                    {otpSent ? '발송됨' : '발송'}
                  </Button>
                </div>
                {otpSent && (
                  <div className="flex items-start gap-2">
                    <Input
                      value={otpCode}
                      onChange={(e) => setOtpCode(e.target.value)}
                      placeholder="인증번호 6자리"
                      maxLength={6}
                      width="fill"
                      error={verifyError ?? undefined}
                    />
                    <Button variant="primary" onClick={handleOtpVerify} className="shrink-0">인증</Button>
                  </div>
                )}
              </>
            ) : (
              <div className="flex items-start gap-2">
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="비밀번호 입력"
                  width="fill"
                  error={verifyError ?? undefined}
                />
                <Button variant="primary" onClick={handlePasswordVerify} className="shrink-0">인증</Button>
              </div>
            )}

            {/* OTP 발송 단계의 오류는 아직 입력칸에 붙일 수 없어 여기 남는다 */}
            {isSns && !otpSent && verifyError && <Alert kind="error">{verifyError}</Alert>}
            </div>
          </Card>
        )}

        {/* 본인 확인 완료 표시 */}
        {verified && (
          <Alert kind="success" className="mb-4">본인 확인이 완료됐어요</Alert>
        )}

        {/* 동의 체크박스 */}
        {showVerify && (
          <Checkbox
            className="mb-4"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            label="위 내용을 확인하였으면 탈퇴에 동의합니다."
          />
        )}

        {submitError && <Alert kind="error" className="mb-3">{submitError}</Alert>}

        {/* 버튼 영역 */}
        <div className="flex gap-3">
          <Button variant="primary" size="lg" onClick={() => navigate('/settings/profile')} className="flex-1">
            취소
          </Button>
          {showVerify && (
            <Button
              variant="etc"
              size="lg"
              onClick={handleWithdraw}
              disabled={!verified || !agreed || withdrawMutation.isPending}
              loading={withdrawMutation.isPending}
              className="flex-1"
            >
              탈퇴 확인
            </Button>
          )}
        </div>

        {/* 사용자명 표시 (보조 정보) */}
        {user?.memberName && (
          <p className="mt-5 text-center text-caption text-text-muted">
            탈퇴 대상 계정: <strong>{user.memberName}</strong>
          </p>
        )}
    </Page>
  );
}
